import React from 'react';
import { ComputerDesktopIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

/*
 * Two shapes of the same control.
 *
 *   <ThemeToggle />          — one icon button, flips light ⇄ dark. Used in
 *                              headers where space is tight.
 *   <ThemeSegmentedControl/> — System / Light / Dark. Used where the user is
 *                              already in a settings-ish context (the sidebar),
 *                              because it is the only way back to "follow OS".
 */

const OPTIONS = [
  { value: 'system', label: 'System', Icon: ComputerDesktopIcon },
  { value: 'light', label: 'Light', Icon: SunIcon },
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
];

export default function ThemeToggle({ className = '', iconClassName = 'h-5 w-5' }) {
  const { theme, toggleTheme } = useTheme();
  const nextLabel = theme === 'dark' ? 'light' : 'dark';
  const Icon = theme === 'dark' ? SunIcon : MoonIcon;

  return (
    <button
      type="button"
      onClick={toggleTheme}
      title={`Switch to ${nextLabel} mode`}
      aria-label={`Switch to ${nextLabel} mode`}
      className={`rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${className}`}
    >
      <Icon className={iconClassName} aria-hidden="true" />
    </button>
  );
}

export function ThemeSegmentedControl({ className = '' }) {
  const { preference, setPreference } = useTheme();

  return (
    <div
      role="group"
      aria-label="Color theme"
      className={`flex items-center gap-1 rounded-lg border border-gray-800 bg-gray-900 p-1 ${className}`}
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setPreference(value)}
            aria-pressed={active}
            title={`${label} theme`}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              active ? 'bg-gray-700 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
